import { z } from "zod";
import { MEMBERSHIP_STATUSES } from "../constants/membership.constants";
import { memberCoreSchema } from "./memberCore.schemas";

/**
 * Standard response schema for error handling
 */
export const responseSchema = z.object({
	message: z.coerce.string(),
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean", {
			message:
				"Query param must be 'true' or '1', otherwise it will default to false",
		}),
});

/**
 * Pagination schema for member core endpoints
 */
export const memberCorePaginationSchema = z.object({
	pp: z.coerce
		.number()
		.int()
		.min(1)
		.max(100)
		.default(20)
		.describe("Items per page"),
	p: z.coerce.number().int().min(1).default(1).describe("Page number"),
	sort: z.enum(["asc", "desc"]).default("desc").describe("Sort order"),
	sortKey: z.coerce.string().default("memberId").describe("Key to sort by"),
	count: z.coerce.number().int().optional().describe("Total count of records"),
});

/**
 * Member browse response schema for paginated results
 */
export const memberCoreResponseSchema = z.object({
	rows: z.array(memberCoreSchema).describe("Array of member records"),
	pp: z.coerce.number().optional().describe("Items per page"),
	p: z.coerce.number().optional().describe("Current page number"),
	count: z.coerce.number().optional().describe("Total count of records"),
	sort: z.coerce.string().optional().describe("Sort order"),
	sortKey: z.coerce.string().optional().describe("Sort key"),
});

// ============================================================================
// Browse Members Endpoint Schemas
// ============================================================================

/**
 * Browse members query parameters schema
 * GET /members endpoint
 */
export const browseMembersQueryParamsSchema = z
	.object({
		// Pagination parameters
		pp: z.coerce
			.number()
			.int()
			.min(1)
			.max(100)
			.default(20)
			.optional()
			.describe("Items per page"),
		p: z.coerce
			.number()
			.int()
			.min(1)
			.default(1)
			.optional()
			.describe("Page number"),
		sort: z
			.enum(["asc", "desc"])
			.default("desc")
			.optional()
			.describe("Sort order"),
		sortKey: z.coerce
			.string()
			.default("memberId")
			.optional()
			.describe("Key to sort by"),

		// Filter parameters
		lineName: z.coerce
			.string()
			.optional()
			.describe("Query parameter for LINE display name"),
		messages: z
			.enum(["read", "unread"])
			.optional()
			.describe("Filter by message status"),
		isFriends: z.coerce
			.string()
			.transform((val) => val === "true" || val === "1")
			.refine((val) => typeof val === "boolean", {
				message:
					"Query param must be 'true' or '1', otherwise it will default to false",
			})
			.optional()
			.describe("Query parameter for friend status"),
		isRegistered: z.coerce
			.string()
			.transform((val) => val === "true" || val === "1")
			.refine((val) => typeof val === "boolean", {
				message:
					"Query param must be 'true' or '1', otherwise it will default to false",
			})
			.optional()
			.describe("Query parameter for registration status"),
		membershipStatus: z
			.enum(MEMBERSHIP_STATUSES)
			.optional()
			.describe("Filter by normalized membership status"),
		memberCode: z.coerce
			.string()
			.optional()
			.describe("Query parameter for member code"),
		furigana: z.coerce
			.string()
			.optional()
			.describe("Query parameter for member furigana"),

		// Date range filters
		createdAtMin: z.coerce
			.string()
			.optional()
			.describe("Query parameter for minimum creation date"),
		createdAtMax: z.coerce
			.string()
			.optional()
			.describe("Query parameter for maximum creation date"),
		memberSinceMin: z.coerce
			.string()
			.optional()
			.describe("Query parameter for minimum member since date"),
		memberSinceMax: z.coerce
			.string()
			.optional()
			.describe("Query parameter for maximum member since date"),
		lastVisitMin: z.coerce
			.string()
			.optional()
			.describe("Query parameter for minimum last visit date"),
		lastVisitMax: z.coerce
			.string()
			.optional()
			.describe("Query parameter for maximum last visit date"),
	})
	.passthrough() // Allow member attribute filters
	.optional();

/**
 * @description A paginated list of filtered members
 */
export const browseMembers200Schema = memberCoreResponseSchema;

/**
 * @description Bad request
 */
export const browseMembers400Schema = z.lazy(() => responseSchema);

/**
 * @description Internal server error
 */
export const browseMembers500Schema = z.lazy(() => responseSchema);

// ============================================================================
// List Members Endpoint Schemas
// ============================================================================

/**
 * @description Simple list of all members
 */
export const listMembers200Schema = z.array(memberCoreSchema);

/**
 * @description Bad request
 */
export const listMembers400Schema = z.lazy(() => responseSchema);

/**
 * @description Internal server error
 */
export const listMembers500Schema = z.lazy(() => responseSchema);

// ============================================================================
// Get Member Endpoint Schemas
// ============================================================================

/**
 * Get member path parameters schema
 * GET /members/:memberId endpoint
 */
export const getMemberPathParamsSchema = z.object({
	memberId: z.coerce.string().describe("ID of member to return"),
});

/**
 * @description Member details retrieved successfully
 */
export const getMember200Schema = memberCoreSchema.extend({
	countVisit: z.coerce.number().optional().describe("Total unique visit days"),
	lastVisit: z.coerce.date().optional().describe("Last visit date"),
	kakeruPoints: z.array(z.any()).optional().describe("Point history"),
});

/**
 * @description Member not found
 */
export const getMember404Schema = z.lazy(() => responseSchema);

/**
 * @description Bad request
 */
export const getMember400Schema = z.lazy(() => responseSchema);

/**
 * @description Internal server error
 */
export const getMember500Schema = z.lazy(() => responseSchema);

// ============================================================================
// Update Member Endpoint Schemas
// ============================================================================

/**
 * Update member path parameters schema
 * PUT /members/:memberId endpoint
 */
export const updateMemberPathParamsSchema = z.object({
	memberId: z.coerce.string().describe("ID of member to update"),
});

/**
 * Update member request body schema
 * PUT /members/:memberId endpoint
 */
export const updateMemberMutationRequestSchema = z
	.object({
		activeUntil: z.coerce.string().optional().describe("Active until date"),
		remarks: z.coerce.string().optional().describe("Member remarks"),
		memberSince: z.coerce.string().optional().describe("Member since date"),
		kakeruPoint: z.coerce.number().optional().describe("Kakeru point value"),
		kakeruPointIsAdd: z.coerce
			.boolean()
			.optional()
			.describe("Whether to add or subtract points"),
		visitDate: z.coerce.string().optional().describe("Visit date"),
		memberVisitId: z.coerce.number().optional().describe("Member visit ID"),
	})
	.passthrough(); // Allow additional member attribute fields

/**
 * @description Member updated successfully
 */
export const updateMember200Schema = z.object({
	success: z.boolean().describe("Update success status"),
});

/**
 * @description Member not found
 */
export const updateMember404Schema = z.lazy(() => responseSchema);

/**
 * @description Bad request
 */
export const updateMember400Schema = z.lazy(() => responseSchema);

/**
 * @description Internal server error
 */
export const updateMember500Schema = z.lazy(() => responseSchema);

// ============================================================================
// Delete Member Endpoint Schemas
// ============================================================================

/**
 * Delete member path parameters schema
 * DELETE /members/:memberId endpoint
 */
export const deleteMemberPathParamsSchema = z.object({
	memberId: z.coerce.string().describe("ID of member to delete"),
});

/**
 * @description Member deleted successfully
 */
export const deleteMember200Schema = z.object({
	success: z.boolean().describe("Deletion success status"),
});

/**
 * @description Member not found
 */
export const deleteMember404Schema = z.lazy(() => responseSchema);

/**
 * @description Bad request
 */
export const deleteMember400Schema = z.lazy(() => responseSchema);

/**
 * @description Internal server error
 */
export const deleteMember500Schema = z.lazy(() => responseSchema);

// ============================================================================
// Generate Member CSV Endpoint Schemas
// ============================================================================

/**
 * Generate member CSV request body schema
 * POST /members/csv endpoint
 */
export const generateMemberCSVMutationRequestSchema = z.object({
	password: z.string().min(1).describe("Password for CSV generation"),
	isCampaign: z.coerce
		.boolean()
		.optional()
		.default(false)
		.describe("Whether to include campaign members"),
});

/**
 * @description CSV file generated successfully
 */
export const generateMemberCSV200Schema = z.any(); // Binary response

/**
 * @description Bad request
 */
export const generateMemberCSV400Schema = z.lazy(() => responseSchema);

/**
 * @description Unauthorized - incorrect password
 */
export const generateMemberCSV401Schema = z.lazy(() => responseSchema);

/**
 * @description Internal server error
 */
export const generateMemberCSV500Schema = z.lazy(() => responseSchema);

// ============================================================================
// Convenience Aliases
// ============================================================================

export const browseMembersQueryResponseSchema = z.lazy(
	() => browseMembers200Schema,
);
export const listMembersQueryResponseSchema = z.lazy(
	() => listMembers200Schema,
);
export const getMemberQueryResponseSchema = z.lazy(() => getMember200Schema);
export const updateMemberMutationResponseSchema = z.lazy(
	() => updateMember200Schema,
);
export const deleteMemberMutationResponseSchema = z.lazy(
	() => deleteMember200Schema,
);
export const generateMemberCSVMutationResponseSchema = z.lazy(
	() => generateMemberCSV200Schema,
);
